<template>
  <div class="income-chart preloader-container">
    {{
      changeLang /* Данное вычисляемое свойство ничего не возвращает. Оно находится здесь, чтобы при изменении в нем данных отпрабатывал код внтури функции */
    }}
    <div v-show="isIncomeLoading" class="preloader income-card__preloader">
      <spinner :animation-duration="1000" :size="60" :color="'#2E79EF'" />
    </div>
    <div class="row justify-between align-center income-chart__top-bar">
      <div>
        <h4 class="heading">
          {{ $t("profitability title") }}, {{ getSelectedCurrency }}
        </h4>
        <span class="income_type_container">
          <span
            class="income_type_button"
            @click="handleIncomeTypeChange(0)"
            :class="{ active: this.incomeType == 0 }"
          >
            {{ $t("accumulated") }}
          </span>
          |
          <span
            class="income_type_button"
            @click="handleIncomeTypeChange(1)"
            :class="{ active: this.incomeType == 1 }"
          >
            {{ $t("annual") }}
          </span>
        </span>
      </div>
      <div class="overview__controls controls row">
        <horizontal-button-group
          :options="[
            { value: 'week', text: $t('week') },
            { value: 'month', text: $t('month') },
            { value: 'quarter', text: $t('quarter') },
            { value: 'year', text: $t('year') }
          ]"
          @handleClick="changeSelect"
          class="controls__item controls__date-template-picker hide-tablet hide-mobile"
        />
        <date-picker
          :lang="$t('short lang')"
          :range="true"
          :confirm="true"
          class="controls__item controls__date-picker"
          value-type="format"
          :value="incomeDateRange"
          @change="handleDatePickerChange"
          :clearable="false"
          :placeholder="$t('select date range')"
          :not-after="new Date()"
          format="DD.MM.YYYY"
        />
      </div>
    </div>
    <highchart
      v-if="this.incomeErrorMessage === ''"
      :options="incomeChartOptions"
      class="income-chart__chart"
      :class="{ 'income-chart__chart_hidden': isIncomeLoading }"
    />
    <div v-else class="income__error">
      <p class="error_text">{{ this.incomeErrorMessage }}</p>
    </div>
  </div>
</template>

<script>
import { Chart } from "highcharts-vue";
import HighCharts from "highcharts";
import {
  months,
  shortMonths,
  weekdays,
  shortWeekdays
} from "@/config/dateTranslations";
import DatePicker from "vue2-datepicker";
import { mapGetters } from "vuex";
import moment from "moment";
import { HalfCircleSpinner } from "epic-spinners";
import HorizontalButtonsGroup from "@/components/ui-kit/HorizontalButtonsGroup";
import i18n from "@/lib/vue-i18n/index";

export default {
  components: {
    highchart: Chart,
    "date-picker": DatePicker,
    spinner: HalfCircleSpinner,
    "horizontal-button-group": HorizontalButtonsGroup
  },
  data() {
    return {
      selectType: "months",
      incomeType: 0
    };
  },
  computed: {
    // При изменении выбранного языка переводим график. Данные повторно загружаются при выборе другого языка, чтобы вызвать перезагрузку графика
    changeLang() {
      let selectedLang = i18n.locale;
      // Переводим график
      HighCharts.setOptions({
        lang: {
          months: months[selectedLang],
          shortMonths: shortMonths[selectedLang],
          weekdays: weekdays[selectedLang],
          shortWeekdays: shortWeekdays[selectedLang]
        }
      });
      return null;
    },
    ...mapGetters([
      "incomeChartOptions",
      "getSelectedCurrency",
      //"getSelectedCurrencySign",
      "isIncomeLoading",
      "incomeDateRange",
      "incomeErrorMessage"
    ])
  },
  methods: {
    handleIncomeTypeChange(type) {
      this.incomeType = type;
      this.$store.dispatch("setIncomeType", type);
    },
    handleDatePickerChange(dateRange) {
      // Очищаем данные о доходности, чтобы отобразить лоадер
      this.$store.dispatch("clearIncomeData");
      // Отправляем новую дату в глобальное хранилище
      this.setSelectedDateRange(dateRange);
      // Загружаем данные о доходности на основе выбранного промежутка даты
      this.$store.dispatch("fetchIncomeData");
    },
    // Устанавливаем дату в глобальное хранилище
    setSelectedDateRange(dateRange) {
      this.$store.dispatch("setIncomeDateRange", dateRange);
    },
    // Обрабатываем выбор шаблона даты. Возможные варианты: неделя, месяц, квартал и год
    changeSelect(value) {
      // Очищаем данные о доходности, чтобы показался лоадер
      this.$store.dispatch("clearIncomeData");
      this.selectType = value;
      switch (value) {
        case "week":
          this.setSelectedDateRange([
            moment()
              .subtract(7, "days")
              .format("DD.MM.YYYY"),
            moment().format("DD.MM.YYYY")
          ]);
          break;
        case "month":
          this.setSelectedDateRange([
            moment()
              .subtract(1, "months")
              .format("DD.MM.YYYY"),
            moment().format("DD.MM.YYYY")
          ]);
          break;
        case "quarter":
          this.setSelectedDateRange([
            moment()
              .subtract(3, "months")
              .format("DD.MM.YYYY"),
            moment().format("DD.MM.YYYY")
          ]);
          break;
        case "year":
          this.setSelectedDateRange([
            moment()
              .subtract(1, "years")
              .format("DD.MM.YYYY"),
            moment().format("DD.MM.YYYY")
          ]);
          break;
      }
      // Загружаем данные о доходности
      this.$store.dispatch("fetchIncomeData");
    }
  },
  created() {
    // При инициализации компонента определяем выбранный язык
    let selectedLang = i18n.locale;

    HighCharts.setOptions({
      lang: {
        months: months[selectedLang],
        shortMonths: shortMonths[selectedLang],
        weekdays: weekdays[selectedLang],
        shortWeekdays: shortWeekdays[selectedLang]
      }
    });
  },
  mounted() {
    // Прогружаем данные при установке компонента
    this.$store.dispatch("fetchIncomeData");
  }
};
</script>

<style scoped>
.heading {
  font-size: 36px;
  font-weight: normal;
  color: #222c44;
}

.heading__profitability {
  margin-left: 30px;
}

.heading_green {
  color: #5ac480;
}

.heading_red {
  color: red;
}

.income-chart__chart {
  margin-top: 30px;
}

.income-chart__chart_hidden {
  visibility: hidden;
}

.income-chart__prealoder {
  z-index: 5;
}

.controls__item {
  height: 34px;
  box-sizing: border-box;
}

.controls__date-picker {
  padding: 0 20px;
}

@media (max-width: 600px) {
  .income-chart__top-bar {
    flex-direction: column;
    align-items: flex-start;
  }

  .controls__date-picker {
    margin-top: 10px;
    padding: 0;
  }
}

.income__error {
  width: 100%;
  height: 430px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.error_text {
  text-align: center;
  color: #dc3545;
}
.income_type_button {
  cursor: pointer;
}
.active {
  color: #529fff;
  text-decoration: underline;
}
.income_type_container {
  color: #2e79ef;
  font-size: 13pt;
}
</style>
